import { useMemo, useState } from "react";
import { topicComments } from "@/mocks/comments";
import type { FloorComment } from "@/types/content";
import Reveal from "@/components/base/Reveal";

type SortKey = "hot" | "new";

type CommentSectionProps = {
  topicId: string;
  topicTitle: string;
};

let uid = 0;

export default function CommentSection({ topicId, topicTitle }: CommentSectionProps) {
  const baseComments: FloorComment[] = topicComments[topicId] || [];
  const [extraComments, setExtraComments] = useState<FloorComment[]>([]);
  const [liked, setLiked] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("hot");
  const [nickname, setNickname] = useState("");
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");

  const comments = useMemo(() => {
    const all = [...extraComments, ...baseComments];
    if (sortKey === "hot") {
      return [...all].sort((a, b) => b.likes - a.likes);
    }
    return all;
  }, [baseComments, extraComments, sortKey]);

  const toggleLike = (id: string) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleReply = (user: string, id: string) => {
    setReplyTo(id);
    setDraft(`@${user} `);
    setFeedback("");
    document.getElementById("comment-editor")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) {
      setFeedback("说点什么再发布吧。");
      return;
    }
    if (content.length > 500) {
      setFeedback("评论内容不能超过 500 字。");
      return;
    }
    uid += 1;
    const newComment: FloorComment = {
      id: `local-${uid}`,
      user: nickname.trim() || "深夜路人",
      level: "围观群众",
      time: "刚刚",
      content,
      likes: 0,
    };
    setExtraComments((prev) => [newComment, ...prev]);
    setDraft("");
    setNickname("");
    setReplyTo(null);
    setFeedback("发布成功，你的观点已经出现在评论区。");
  };

  return (
    <section
      id="comments"
      className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16"
      aria-label="评论区"
    >
      <div className="flex flex-col gap-4 border-b border-background-200/70 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-primary-500">
            <i className="ri-chat-quote-line" />
            COMMENT AREA
          </span>
          <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
            评论区 · 观点楼层
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            围绕「{topicTitle}」的 {comments.length + 1264} 条讨论，先看别人怎么想。
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-background-200/70 p-1">
          <button
            type="button"
            onClick={() => setSortKey("hot")}
            className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
              sortKey === "hot" ? "bg-background-50 text-foreground-950" : "text-foreground-600"
            }`}
          >
            <i className="ri-fire-line" />
            最热
          </button>
          <button
            type="button"
            onClick={() => setSortKey("new")}
            className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
              sortKey === "new" ? "bg-background-50 text-foreground-950" : "text-foreground-600"
            }`}
          >
            <i className="ri-time-line" />
            最新
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <ul className="flex flex-col gap-4">
            {comments.map((comment, index) => {
              const isLiked = liked.includes(comment.id);
              return (
                <Reveal key={comment.id} delay={Math.min(index, 6) * 40}>
                  <li className="flex gap-4 rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-500">
                      {comment.user.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <header className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-950">{comment.user}</span>
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] text-accent-500">
                          {comment.level}
                        </span>
                        <span className="text-xs text-foreground-500">{comment.time}</span>
                      </header>
                      <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">
                        {comment.content}
                      </p>
                      <footer className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                        <button
                          type="button"
                          onClick={() => toggleLike(comment.id)}
                          aria-pressed={isLiked}
                          className={`flex items-center gap-1.5 transition-colors ${
                            isLiked ? "text-primary-500" : "hover:text-primary-500"
                          }`}
                        >
                          <i className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                          {comment.likes + (isLiked ? 1 : 0)}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReply(comment.user, comment.id)}
                          className="flex items-center gap-1.5 transition-colors hover:text-accent-500"
                        >
                          <i className="ri-reply-line" />
                          回复
                        </button>
                        {replyTo === comment.id && (
                          <span className="text-accent-500">正在回复这一层…</span>
                        )}
                      </footer>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ul>
        </div>

        <div className="lg:col-span-4" id="comment-editor">
          <form
            onSubmit={handleSubmit}
            className="sticky top-24 rounded-lg border border-background-200/80 bg-background-100 p-5"
          >
            <h3 className="heading-serif text-base font-bold text-foreground-950">发表你的看法</h3>
            <p className="mt-1.5 text-xs text-foreground-500">
              观点可以尖锐，但请对准事情本身。最多 500 字。
            </p>

            <label className="mt-4 block text-xs text-foreground-600" htmlFor="nickname">
              昵称（可选）
            </label>
            <input
              id="nickname"
              name="nickname"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={16}
              placeholder="留空则显示为「深夜路人」"
              className="mt-1.5 w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
            />

            <label className="mt-4 block text-xs text-foreground-600" htmlFor="content">
              你的观点
            </label>
            <textarea
              id="content"
              name="content"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setFeedback("");
              }}
              rows={5}
              maxLength={500}
              placeholder="这件事你怎么看？补充事实、提出质疑，或者给出一个别人没想到的角度。"
              className="mt-1.5 w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
            />
            <div className="mt-1.5 flex items-center justify-between text-xs text-foreground-500">
              <span>{draft.length} / 500</span>
              {replyTo && (
                <button
                  type="button"
                  onClick={() => {
                    setReplyTo(null);
                    setDraft("");
                  }}
                  className="text-accent-500"
                >
                  取消回复
                </button>
              )}
            </div>

            {feedback && (
              <p
                className={`mt-3 rounded-md px-3 py-2 text-xs ${
                  feedback.includes("成功")
                    ? "bg-secondary-100/70 text-secondary-900"
                    : "bg-primary-100/70 text-primary-700"
                }`}
              >
                {feedback}
              </p>
            )}

            <button
              type="submit"
              className="mt-4 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              <i className="ri-send-plane-fill" />
              发布评论
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}